import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, CreditCard, Loader2, ShieldCheck } from "lucide-react";
import api, { PLATFORM_NAME, errMsg, money, openCheckout } from "@/lib/api";
import { useRiderScope } from "@/lib/riderScope";
import { SandboxNotice } from "@/components/SandboxNotice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** The passenger's own payment page — pick a gratuity, then go to the secure card form. */
export default function PayTrip() {
  const { companyId, token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [tipPercent, setTipPercent] = useState(null);
  const [customTip, setCustomTip] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [saveCard, setSaveCard] = useState(false);
  const handle = data?.company?.handle || data?.company?.id || companyId;
  useRiderScope(handle, `pay/${token}`);

  const load = useCallback(
    () => api.get(`/public/pay/${token}`).then(({ data: d }) => setData(d))
      .catch((e) => setError(errMsg(e))),
    [token]
  );
  useEffect(() => { load(); }, [load]);

  // Coming back from the card form: confirm with the processor, not with the browser.
  useEffect(() => {
    if (!params.get("paid")) return undefined;
    const check = () => api.get(`/public/pay/${token}/status`).then(() => load()).catch(() => {});
    check();
    const timer = setTimeout(check, 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, params]);

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-900 p-6">
        {error ? <p data-testid="pay-error" className="text-center text-sm text-red-400">{error}</p>
          : <Loader2 className="h-5 w-5 animate-spin text-gold" />}
      </div>
    );
  }

  const c = data.company;
  const tip = data.allow_tip
    ? (tipPercent ? Math.round(data.amount * tipPercent) / 100 : Number(customTip || 0))
    : 0;
  const total = Math.round((data.amount + tip) * 100) / 100;

  const pay = async () => {
    setBusy(true); setError(""); setCheckoutUrl("");
    try {
      const { data: res } = await api.post(`/public/pay/${token}/checkout`, { tip, save_card: saveCard });
      if (!openCheckout(res.checkout_url, setCheckoutUrl)) setCheckoutUrl(res.checkout_url);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="pay-page" className="min-h-screen bg-ink-900 px-4 py-10 text-slate-200">
      <div className="mx-auto max-w-md space-y-5 rise">
        <div className="rounded-2xl border border-ink-600 bg-ink-800 p-6"
          style={{ borderTop: `3px solid ${c.branding?.primary_color || "#D4AF37"}` }}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">{c.name}</p>
              <h1 className="mt-2 text-2xl font-bold text-slate-50">
                {data.kind === "tip" ? "Add a gratuity" : "Pay for your trip"}
              </h1>
              {data.trip?.number && (
                <p className="mt-1 text-xs text-slate-500">
                  Trip #{data.trip.number} · {data.trip.trip_date} {data.trip.pickup_time}
                </p>
              )}
            </div>
            {c.logo_url && <img src={c.logo_url} alt={`${c.name} logo`} className="h-11 w-auto object-contain" />}
          </div>
          {data.trip?.pickup && (
            <p className="mt-4 text-xs leading-relaxed text-slate-400">
              {data.trip.pickup} → {data.trip.dropoff}
            </p>
          )}
        </div>

        {data.paid ? (
          <div data-testid="pay-paid" className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-6 text-center">
            <CheckCircle2 className="mx-auto h-7 w-7 text-emerald-400" />
            <p className="mt-3 text-lg font-semibold text-slate-50">
              {data.settled
                ? "This trip is settled — thank you."
                : `${money(data.charged || data.amount)} paid — thank you.`}
            </p>
            {data.settled && (
              <p data-testid="pay-settled" className="mt-1 text-sm text-emerald-300">
                The balance was already taken from the card on file, so there is nothing left to pay.
              </p>
            )}
            {data.tip > 0 && (
              <p data-testid="pay-paid-tip" className="mt-1 text-sm text-emerald-300">
                Including a {money(data.tip)} gratuity for {data.trip?.chauffeur_name || "your chauffeur"}.
              </p>
            )}
            <p className="mt-3 text-xs text-slate-400">A receipt is on its way to your email.</p>
          </div>
        ) : (
          <div className="space-y-5 rounded-2xl border border-ink-600 bg-ink-800 p-6">
            <div className="flex items-baseline justify-between">
              <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">
                {data.kind === "tip" ? "Gratuity" : "Amount due"}
              </span>
              <span data-testid="pay-amount" className="font-display text-3xl font-bold text-slate-50">
                {money(data.kind === "tip" ? data.tip : data.amount)}
              </span>
            </div>

            {data.allow_tip && (
              <div className="border-t border-ink-600 pt-5">
                <p className="font-mono text-[11px] uppercase tracking-widest text-gold">
                  Add a gratuity
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  100% goes to {data.trip?.chauffeur_name || "your chauffeur"}.
                </p>
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {(data.tip_percents || []).map((p) => (
                    <button key={p} type="button" data-testid={`pay-tip-${p}`}
                      onClick={() => { setTipPercent(p); setCustomTip(""); }}
                      className={`h-14 rounded-xl border text-sm transition-colors ${
                        tipPercent === p
                          ? "border-gold/60 bg-gold/15 text-gold"
                          : "border-ink-600 bg-ink-900 text-slate-300 hover:border-gold/40"
                      }`}>
                      <span className="block font-semibold">{p}%</span>
                      <span className="block font-mono text-[11px] opacity-80">
                        {money((data.amount * p) / 100)}
                      </span>
                    </button>
                  ))}
                  <button type="button" data-testid="pay-tip-none"
                    onClick={() => { setTipPercent(null); setCustomTip(""); }}
                    className={`h-14 rounded-xl border text-sm transition-colors ${
                      !tipPercent && !Number(customTip)
                        ? "border-gold/60 bg-gold/15 text-gold"
                        : "border-ink-600 bg-ink-900 text-slate-300 hover:border-gold/40"
                    }`}>
                    No tip
                  </button>
                </div>
                <Input data-testid="pay-tip-custom" type="number" min="0" step="1" value={customTip}
                  onChange={(e) => { setCustomTip(e.target.value); setTipPercent(null); }}
                  placeholder="Other amount"
                  className="mt-2 h-12 border-ink-600 bg-ink-900 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold" />
              </div>
            )}

            {data.can_save_card && (
              <label data-testid="pay-save-card-label" className="flex cursor-pointer items-start gap-3 rounded-xl border border-ink-600 bg-ink-900/60 p-4">
                <input data-testid="pay-save-card" type="checkbox" checked={saveCard}
                  onChange={(e) => setSaveCard(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[#D4AF37]" />
                <span className="text-xs leading-relaxed text-slate-300">
                  Keep my card on file and settle the rest of my fare after the trip.
                  <span className="block text-slate-500">
                    {c.name} will charge the remaining balance to this card about{" "}
                    {data.grace_minutes || 30} minutes after your trip finishes. Nothing extra is
                    taken today and you can still add a gratuity afterwards.
                  </span>
                </span>
              </label>
            )}

            <div className="flex items-baseline justify-between border-t border-ink-600 pt-4">
              <span className="text-sm text-slate-300">Total</span>
              <span data-testid="pay-total" className="font-display text-2xl font-bold text-gold">
                {money(total)}
              </span>
            </div>

            {data.sandbox && <SandboxNotice testId="pay-sandbox" />}
            {error && <p data-testid="pay-checkout-error" className="text-sm text-red-400">{error}</p>}

            {!data.payments_ready ? (
              <p data-testid="pay-not-ready" className="text-sm text-slate-400">
                {c.name} cannot take card payments online right now. Please call{" "}
                {c.phone || "the office"} to settle this trip.
              </p>
            ) : (
              <>
                <Button data-testid="pay-submit" disabled={busy || total <= 0} onClick={pay}
                  className="h-16 w-full bg-gold text-base font-semibold text-ink-900 hover:bg-gold-light">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" />
                    : <><CreditCard className="mr-2 h-5 w-5" /> Pay {money(total)} by card</>}
                </Button>
                {checkoutUrl && (
                  <a data-testid="pay-checkout-link" href={checkoutUrl} target="_blank" rel="noopener noreferrer"
                    className="block rounded-xl border border-gold/40 bg-gold/10 p-4 text-center text-sm font-semibold text-gold">
                    Open the secure card form →
                  </a>
                )}
              </>
            )}
            <p className="flex items-center justify-center gap-1.5 text-center font-mono text-[10px] uppercase tracking-widest text-slate-600">
              <ShieldCheck className="h-3 w-3" /> Card details go straight to Stripe
            </p>
          </div>
        )}

        {c.show_powered_by && (
          <p className="text-center font-mono text-[11px] text-slate-600">Powered by {PLATFORM_NAME}</p>
        )}
      </div>
    </div>
  );
}
